import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  createEmploymentContractApi,
  fetchEmploymentContractsApi,
  downloadEmploymentContractApi,
  CreateEmploymentContractPayload,
  CreateEmploymentContractResponse,
} from './employment-contract.service';

interface EmploymentContractState {
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  message: string;
  contractInfo: any;
  isFetching: boolean;
  isDownloading: boolean;
}

const initialState: EmploymentContractState = {
  isLoading: false,
  isSuccess: false,
  isError: false,
  message: '',
  contractInfo: null,
  isFetching: false,
  isDownloading: false,
};

export const fetchEmploymentContracts = createAsyncThunk<any, void, { rejectValue: string }>(
  'employmentContract/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchEmploymentContractsApi();
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch employment contracts');
    }
  }
);

export const downloadEmploymentContract = createAsyncThunk<void, { contractId: number | string; lang: string }, { rejectValue: string }>(
  'employmentContract/download',
  async ({ contractId, lang }, { rejectWithValue }) => {
    try {
      await downloadEmploymentContractApi(contractId, lang);
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to download employment contract');
    }
  }
);

export const createEmploymentContract = createAsyncThunk<
  CreateEmploymentContractResponse,
  CreateEmploymentContractPayload,
  { rejectValue: string }
>(
  'employmentContract/create',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createEmploymentContractApi(payload);
      return response;
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to submit employment contract';
      return rejectWithValue(message);
    }
  }
);

const employmentContractSlice = createSlice({
  name: 'employmentContract',
  initialState,
  reducers: {
    resetEmploymentContractState: (state) => {
      state.isLoading = false;
      state.isSuccess = false;
      state.isError = false;
      state.message = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmploymentContracts.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(fetchEmploymentContracts.fulfilled, (state, action) => {
        state.isFetching = false;
        state.contractInfo = action.payload?.response ?? action.payload ?? null;
      })
      .addCase(fetchEmploymentContracts.rejected, (state) => {
        state.isFetching = false;
      })
      .addCase(downloadEmploymentContract.pending, (state) => {
        state.isDownloading = true;
      })
      .addCase(downloadEmploymentContract.fulfilled, (state) => {
        state.isDownloading = false;
      })
      .addCase(downloadEmploymentContract.rejected, (state) => {
        state.isDownloading = false;
      })
      .addCase(createEmploymentContract.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
        state.message = '';
      })
      .addCase(createEmploymentContract.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.message = action.payload?.message || '';
      })
      .addCase(createEmploymentContract.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload || 'Something went wrong';
      });
  },
});

export const { resetEmploymentContractState } = employmentContractSlice.actions;
export default employmentContractSlice.reducer;
